import React, { FC, useEffect, useState } from 'react'
import { Flex, Text, Divider, Heading, Spinner, Radio, Box, Switch, Button } from '@chakra-ui/react'
import { sendMessageToExtension } from '../../../webapp/utils/externalMessaging'
import { BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS } from '../../../extension/models/messaging.types'
import { useUserContext } from '../../../contexts/UserContext'
import AutoSaveSettingsFormContent from './AutoSaveSettingsFormContent'
import AdminSettingsContent from './AdminSettingsContent'
import { FEATURE_FLAG_KEYS_ENUM } from '../../../models/feature_flags.types'
import { useFeatureFlagContext } from '../../../contexts/FeatureFlagContext'
import InternalSettingsContent from './InternalSettingsContent'
import {
  ExtensionSettingsType,
  EXTENSION_SETTINGS_KEYS,
  CHROME_STORAGE_LOCAL_KEYS,
} from '../../../extension/models/extensionStorage.types'
import { useLocalStorageBoolean } from '../../../utils/genericHooks'
import { WEBAPP_LOCAL_STORAGE_KEYS } from '../../../models/browserStorage.types'
import DeleteUserContent from './DeleteUserSettings'
import { FEEDBACK_FORM_URL, HOW_TO_URL, WEBAPP_BASE_URL } from '../../../constants'
import { useNavigate } from 'react-router-dom'

const SettingsForm: FC = () => {
  const { captureAnalytics, userInfo } = useUserContext()
  const navigate = useNavigate()

  const isPremium = !!userInfo?.is_premium
  const { featureFlags } = useFeatureFlagContext()
  const isDemoAccount = featureFlags[FEATURE_FLAG_KEYS_ENUM.IsDemoAccount]
  const isDeveloper = featureFlags[FEATURE_FLAG_KEYS_ENUM.IsDeveloper]
  const isInternal = featureFlags[FEATURE_FLAG_KEYS_ENUM.IsAlphaUser]
  const shouldShowNewTabPageSettings = featureFlags[FEATURE_FLAG_KEYS_ENUM.ShouldOverrideNewTabPage]
  const isDefaultGuidedAutoSave = featureFlags[FEATURE_FLAG_KEYS_ENUM.IsDefaultGuidedAutoSave]
  const isAbleToDeleteAccount = featureFlags[FEATURE_FLAG_KEYS_ENUM.IsAbleToDeleteAccount]
  const shouldShowASTIncludeUserClosedTabsSetting = true

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [extensionSettings, setExtensionSettings] = useState<
    | Pick<
        ExtensionSettingsType,
        | EXTENSION_SETTINGS_KEYS.STALE_THRESHOLD_AFTER_OVERWHELM
        | EXTENSION_SETTINGS_KEYS.OVERWHELM_THRESHOLD
        | EXTENSION_SETTINGS_KEYS.IS_GUIDED_AUTO_SAVE_ENABLED
        | EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_FREQUENCY_THRESHOLD
        | EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_INACTIVITY_THRESHOLD
        | EXTENSION_SETTINGS_KEYS.IS_NEW_TAB_PAGE_OVERRIDE_ENABLED
      >
    | undefined
  >(undefined)

  const {
    overwhelmThreshold,
    staleThresholdAfterOverwhelm,
    isGuidedAutoSaveEnabled,
    guidedAutoSaveFrequencyThreshold,
    guidedAutoSaveInactivityThreshold,
    isNewTabPageOverrideEnabled,
  } = extensionSettings || {}

  const [shouldASTIncludeUserClosedTabs, setShouldASTIncludeUserClosedTabs] =
    useLocalStorageBoolean(WEBAPP_LOCAL_STORAGE_KEYS.ShouldASTIncludeUserClosedTabs, false)

  const handleShouldASTIncludeUserClosedTabsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldASTIncludeUserClosedTabs(e.target.checked)
    captureAnalytics('settings_page:should_ast_include_user_closed_tabs_change', {
      shouldASTIncludeUserClosedTabs: e.target.checked,
    })
  }

  const handleGuidedAutomaticSaveChange = async (isGuided: boolean) => {
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.IS_GUIDED_AUTO_SAVE_ENABLED]: isGuided,
      },
    })
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.IS_GUIDED_AUTO_SAVE_ENABLED]: isGuided,
          }
        : prev,
    )
    captureAnalytics('settings_page:guided_automatic_save_change', {
      isGuidedAutoSaveEnabled: isGuided,
    })
  }

  const handleChangeStaleThresholdAfterOverwhelm = async (value: number) => {
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.STALE_THRESHOLD_AFTER_OVERWHELM]: value,
      },
    })
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.STALE_THRESHOLD_AFTER_OVERWHELM]: value,
          }
        : prev,
    )
    captureAnalytics('settings_page:stale_threshold_after_overwhelm_change', {
      staleThresholdAfterOverwhelm: value,
    })
  }

  const handleChangeOverwhelmThreshold = async (value: number) => {
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.OVERWHELM_THRESHOLD]: value,
      },
    })
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.OVERWHELM_THRESHOLD]: value,
          }
        : prev,
    )
    captureAnalytics('settings_page:overwhelm_threshold_change', {
      overwhelmThreshold: value,
    })
  }

  const handleChangeGuidedAutoSaveFrequencyThreshold = async (value: number) => {
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_FREQUENCY_THRESHOLD]: value,
      },
    })
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_FREQUENCY_THRESHOLD]: value,
          }
        : prev,
    )
    captureAnalytics('settings_page:guided_auto_save_frequency_threshold_change', {
      guidedAutoSaveFrequencyThreshold: value,
    })
  }

  const handleChangeGuidedAutoSaveInactivityThreshold = async (value: number) => {
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_INACTIVITY_THRESHOLD]: value,
      },
    })
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_INACTIVITY_THRESHOLD]: value,
          }
        : prev,
    )
    captureAnalytics('settings_page:guided_auto_save_inactivity_threshold_change', {
      guidedAutoSaveInactivityThreshold: value,
    })
  }

  const handleNewTabPageOverrideChange = async (_e: React.ChangeEvent<HTMLInputElement>) => {
    setExtensionSettings((prev) =>
      prev
        ? {
            ...prev,
            [EXTENSION_SETTINGS_KEYS.IS_NEW_TAB_PAGE_OVERRIDE_ENABLED]:
              !isNewTabPageOverrideEnabled,
          }
        : prev,
    )

    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.SAVE_EXTENSION_SETTINGS, {
      settings: {
        [EXTENSION_SETTINGS_KEYS.IS_NEW_TAB_PAGE_OVERRIDE_ENABLED]: !isNewTabPageOverrideEnabled,
      },
    })

    captureAnalytics('settings_page:new_tab_page_override_change', {
      isNewTabPageOverrideEnabled: !isNewTabPageOverrideEnabled,
    })
  }

  const onClickGetStarted = () => {
    captureAnalytics('settings:how_to_click')
  }

  const onClickFeedback = () => {
    captureAnalytics('settings:share_feedback')
  }

  const onClickAccountBilling = () => {
    captureAnalytics('settings:my_plan_click', { isPremium })
    navigate('/saved#pricing')
  }

  const restartOnboarding = async () => {
    captureAnalytics('settings:restart_onboarding_click')
    window.localStorage.removeItem(WEBAPP_LOCAL_STORAGE_KEYS.CurrentOnboardingTutorialStepV2)
    window.localStorage.removeItem(WEBAPP_LOCAL_STORAGE_KEYS.CurrentOnboardingTutorialStepV3)
    await sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.UPDATE_CHROME_STORAGE, {
      storageType: 'local',
      items: {
        [CHROME_STORAGE_LOCAL_KEYS.IsOnboardingCompleted]: false,
      },
    })
    window.location.href = WEBAPP_BASE_URL
  }

  useEffect(() => {
    setIsLoading(true)
    sendMessageToExtension(BACKGROUND_ON_MESSAGE_LISTENER_ACTIONS.GET_EXTENSION_SETTINGS)
      .then((response) => {
        const responseSettings = response as ExtensionSettingsType
        setExtensionSettings({
          [EXTENSION_SETTINGS_KEYS.OVERWHELM_THRESHOLD]:
            responseSettings[EXTENSION_SETTINGS_KEYS.OVERWHELM_THRESHOLD],
          [EXTENSION_SETTINGS_KEYS.STALE_THRESHOLD_AFTER_OVERWHELM]:
            responseSettings[EXTENSION_SETTINGS_KEYS.STALE_THRESHOLD_AFTER_OVERWHELM],
          [EXTENSION_SETTINGS_KEYS.IS_GUIDED_AUTO_SAVE_ENABLED]:
            responseSettings[EXTENSION_SETTINGS_KEYS.IS_GUIDED_AUTO_SAVE_ENABLED],
          [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_FREQUENCY_THRESHOLD]:
            responseSettings[EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_FREQUENCY_THRESHOLD],
          [EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_INACTIVITY_THRESHOLD]:
            responseSettings[EXTENSION_SETTINGS_KEYS.GUIDED_AUTO_SAVE_INACTIVITY_THRESHOLD],
          [EXTENSION_SETTINGS_KEYS.IS_NEW_TAB_PAGE_OVERRIDE_ENABLED]:
            responseSettings[EXTENSION_SETTINGS_KEYS.IS_NEW_TAB_PAGE_OVERRIDE_ENABLED],
        })
        setIsLoading(false)
      })
      .catch((e) => {
        console.error('Error fetching extensionSettings', e)
      })
  }, [])

  if (isLoading) {
    return <Spinner size="xl" />
  }

  return (
    <Box>
      {isDefaultGuidedAutoSave && (
        <>
          <Box>
            <Box>
              <Flex mb="20px" w="100%" align={'center'}>
                <Heading as="h2" fontSize="16px" fontWeight={500}>
                  Save
                </Heading>
              </Flex>
              <Radio
                key="automatic-save"
                value="automatic-save"
                isChecked={!isGuidedAutoSaveEnabled}
                onChange={() => handleGuidedAutomaticSaveChange(false)}
                colorScheme="gray"
              >
                <Heading as="h2" fontSize="14px" fontWeight={500} color="#585858" lineHeight="22px">
                  Automatic Save [Recommended]
                </Heading>
              </Radio>
              <Text mt="6px" ml="24px" fontSize="12px" lineHeight="16px" color="#585858">
                {`Skipper will save and close inactive tabs automatically. Perfect for anyone who wants a hands-off experience - recommended for the most decluttered and focused browsing.`}
              </Text>
            </Box>
            <Box mt="12px">
              <Radio
                key="guided-automatic-save"
                value="guided-automatic-save"
                isChecked={isGuidedAutoSaveEnabled}
                onChange={() => handleGuidedAutomaticSaveChange(true)}
                colorScheme="black"
              >
                <Heading as="h2" fontSize="14px" fontWeight={500} color="#585858" lineHeight="22px">
                  Guided Save
                </Heading>
              </Radio>
              <Text mt="6px" ml="24px" fontSize="12px" lineHeight="16px" color="#585858">
                {`Once a day when you return from a break, Skipper will suggest tabs to save and close. You can review these suggestions or dismiss them.`}
              </Text>
            </Box>
          </Box>
        </>
      )}
      <Box mt={4}>
        <AutoSaveSettingsFormContent
          isGuidedAutoSaveEnabled={isGuidedAutoSaveEnabled}
          staleThresholdAfterOverwhelm={staleThresholdAfterOverwhelm}
          overwhelmThreshold={overwhelmThreshold}
          onChangeStaleThresholdAfterOverwhelm={handleChangeStaleThresholdAfterOverwhelm}
          onChangeOverwhelmThreshold={handleChangeOverwhelmThreshold}
        />
      </Box>
      {shouldShowASTIncludeUserClosedTabsSetting && (
        <>
          <Divider my="24px" />
          <Flex mb="20px" w="100%" align={'center'}>
            <Heading as="h2" fontSize="16px" fontWeight={500}>
              Organize
            </Heading>
          </Flex>
          <Flex mb="16px" w="100%" align={'center'}>
            <Heading as="h3" fontSize="14px" fontWeight={500}>
              All saved tabs
            </Heading>
          </Flex>
          <Flex justifyContent="space-between">
            <Text fontSize="12px" lineHeight="16px" pt="4px" color="#585858">
              {`Show tabs I've manually closed in the "All saved tabs" list`}
            </Text>
            <Switch
              isChecked={!!shouldASTIncludeUserClosedTabs}
              onChange={handleShouldASTIncludeUserClosedTabsChange}
            />
          </Flex>
        </>
      )}

      {shouldShowNewTabPageSettings && (
        <>
          <Divider my="24px" />
          <Flex mb="20px" w="100%" align={'center'}>
            <Heading as="h2" fontSize="16px" fontWeight={500}>
              Switch
            </Heading>
          </Flex>
          <Flex mb="16px" w="100%" align={'center'}>
            <Heading as="h3" fontSize="14px" fontWeight={500}>
              New tab page
            </Heading>
          </Flex>
          <Flex justifyContent="space-between">
            <Text fontSize="12px" lineHeight="16px" pt="4px" color="#585858">
              Display my saved Skipper tabs when I open a new tab in Chrome
            </Text>
            <Switch
              isChecked={isNewTabPageOverrideEnabled}
              onChange={handleNewTabPageOverrideChange}
            />
          </Flex>
        </>
      )}

      <>
        <Divider my="24px" />
        <Flex mb="20px" w="100%" align={'center'}>
          <Heading as="h2" fontSize="16px" fontWeight={500}>
            Resources
          </Heading>
        </Flex>
        <Flex gap={2} direction="row" wrap="wrap">
          <Button
            as="a"
            href={HOW_TO_URL}
            target="_blank"
            borderRadius="16px"
            height="28px"
            minWidth="80px"
            background="transparent"
            color="rgba(0, 0, 0, 0.85)"
            width="fit-content"
            fontWeight="500"
            onClick={onClickGetStarted}
            fontSize="12px"
            border="1px solid black"
            _hover={{ bg: '#ebebeb', color: 'rgba(0, 0, 0, 0.85)' }}
          >
            Getting Started Guide
          </Button>
          <Button
            as="a"
            href={FEEDBACK_FORM_URL}
            target="_blank"
            borderRadius="16px"
            height="28px"
            minWidth="80px"
            background="transparent"
            color="rgba(0, 0, 0, 0.85)"
            width="fit-content"
            fontWeight="500"
            onClick={onClickFeedback}
            fontSize="12px"
            border="1px solid black"
            _hover={{ bg: '#ebebeb', color: 'rgba(0, 0, 0, 0.85)' }}
          >
            Share Feedback
          </Button>
          <Button
            borderRadius="16px"
            height="28px"
            minWidth="80px"
            background="transparent"
            color="rgba(0, 0, 0, 0.85)"
            width="fit-content"
            fontWeight="500"
            fontSize="12px"
            border="1px solid black"
            onClick={onClickAccountBilling}
            _hover={{ bg: '#ebebeb', color: 'rgba(0, 0, 0, 0.85)' }}
          >
            Account & Billing
          </Button>
          <Button
            borderRadius="16px"
            height="28px"
            minWidth="80px"
            background="black"
            width="fit-content"
            color="white"
            fontWeight="500"
            fontSize="12px"
            _hover={{ bg: '#585858', color: 'white' }}
            onClick={restartOnboarding}
          >
            Restart Tutorial
          </Button>
        </Flex>
        {isAbleToDeleteAccount ? (
          <Flex mt={8} direction="row">
            <DeleteUserContent />
          </Flex>
        ) : null}
      </>

      {isInternal && (
        <InternalSettingsContent
          guidedAutoSaveFrequencyThreshold={guidedAutoSaveFrequencyThreshold}
          guidedAutoSaveInactivityThreshold={guidedAutoSaveInactivityThreshold}
          onChangeGuidedAutoSaveFrequencyThreshold={handleChangeGuidedAutoSaveFrequencyThreshold}
          onChangeGuidedAutoSaveInactivityThreshold={handleChangeGuidedAutoSaveInactivityThreshold}
        />
      )}
      {(isDemoAccount || isDeveloper) && <AdminSettingsContent />}
    </Box>
  )
}

export default SettingsForm
